<template>
    <div class="artvise-case-form">
        <!-- Confirmation view -->
        <div v-if="showConfirmation" class="confirmation-view" role="status" aria-live="polite">
            <div class="confirmation-content">
                <div v-if="props.confirmationMessage" class="confirmation-message" v-html="props.confirmationMessage">
                </div>
                <div v-else class="confirmation-message">
                    <p>Tack för ditt ärende!</p>
                </div>
            </div>
        </div>

        <!-- Form -->
        <form v-else @submit.prevent="handleSubmit" novalidate>
            <!-- Case type (shown only when multiple case types exist) -->
            <div v-if="parsedCaseTypes.length > 1" class="form-group">
                <label for="caseType" class="required">{{ texts.caseType }}</label>
                <div class="select-container">
                    <select id="caseType" v-model="formData.caseTypeId" :class="{ 'is-invalid': errors.caseTypeId }"
                        @blur="validateField('caseTypeId')">
                        <option value="" disabled>{{ texts.caseTypePlaceholder }}</option>
                        <option v-for="ct in parsedCaseTypes" :key="ct.id" :value="ct.id">{{ ct.name }}</option>
                    </select>
                    <svg class="icon icon-triangel-pil-ned" aria-hidden="true" focusable="false">
                        <use :href="iconUrl"></use>
                    </svg>
                </div>
                <span v-if="errors.caseTypeId" class="error-message">{{ errors.caseTypeId }}</span>
            </div>

            <!-- Email -->
            <div class="form-group">
                <label for="email" class="required">{{ texts.email }}</label>
                <input id="email" type="email" v-model="formData.email" :class="{ 'is-invalid': errors.email }"
                    @blur="validateField('email')" />
                <span v-if="errors.email" class="error-message">{{ errors.email }}</span>
            </div>

            <!-- Surname (Förnamn) -->
            <div class="form-group">
                <label for="surname" class="required">{{ texts.surname }}</label>
                <input id="surname" type="text" v-model="formData.surname" :class="{ 'is-invalid': errors.surname }"
                    @blur="validateField('surname')" />
                <span v-if="errors.surname" class="error-message">{{ errors.surname }}</span>
            </div>

            <!-- Lastname (Efternamn) -->
            <div class="form-group">
                <label for="lastname" class="required">{{ texts.lastname }}</label>
                <input id="lastname" type="text" v-model="formData.lastname" :class="{ 'is-invalid': errors.lastname }"
                    @blur="validateField('lastname')" />
                <span v-if="errors.lastname" class="error-message">{{ errors.lastname }}</span>
            </div>

            <!-- PNR (shown only when block editor has enabled it) -->
            <div v-if="props.showPersonnummer" class="form-group">
                <label for="pnr">{{ texts.pnr }}</label>
                <input id="pnr" type="text" v-model="formData.pnr" :placeholder="pnrPlaceholder"
                    :class="{ 'is-invalid': errors.pnr }" @blur="validateField('pnr')" />
                <span v-if="errors.pnr" class="error-message">{{ errors.pnr }}</span>
            </div>

            <div v-if="props.showCountries" class="form-group">
                <label for="country" class="required">{{ texts.country }}</label>
                <div class="select-container">
                    <select id="country" v-model="formData.country" :class="{ 'is-invalid': errors.country }"
                        @blur="validateField('country')">
                        <option value="" disabled>{{ texts.countryPlaceholder }}</option>
                        <option v-for="country in countryOptions" :key="country.code" :value="country.name">
                            {{ country.name }}
                        </option>
                    </select>
                    <svg class="icon icon-triangel-pil-ned" aria-hidden="true" focusable="false">
                        <use :href="iconUrl"></use>
                    </svg>
                </div>
                <span v-if="errors.country" class="error-message">{{ errors.country }}</span>
            </div>

            <div v-if="props.showExternalCaseNumber" class="form-group">
                <label for="externalCaseNumber">{{ texts.externalCaseNumber }}</label>
                <input id="externalCaseNumber" type="text" v-model="formData.externalCaseNumber"
                    :class="{ 'is-invalid': errors.externalCaseNumber }" @blur="validateField('externalCaseNumber')" />
                <span v-if="errors.externalCaseNumber" class="error-message">{{ errors.externalCaseNumber }}</span>
            </div>

            <div v-if="props.showFileUpload" class="form-group">
                <label for="attachments">{{ texts.fileUpload }}</label>
                <div v-if="hasFileUploadInstructions" class="upload-instructions" v-html="props.fileUploadInstructions"></div>
                <p v-else class="upload-help">{{ texts.fileUploadHelp }}</p>
                <p v-if="props.acceptedFileTypes && !hasFileUploadInstructions" class="upload-help">
                    {{ texts.acceptedFileTypes }}: {{ acceptedFileTypesDisplay }}
                </p>
                <div class="upload-control">
                    <input id="attachments" ref="attachmentsInput" type="file" class="file-input" multiple :accept="acceptAttribute" @change="onFileChange"
                        :class="{ 'is-invalid': errors.attachments }" />
                </div>
                <span v-if="errors.attachments" class="error-message">{{ errors.attachments }}</span>
                <ul v-if="selectedFiles.length" class="upload-file-list">
                    <li v-for="file in selectedFiles" :key="file.key" class="upload-file-item">
                        <span class="upload-file-name">{{ file.name }}</span>
                        <button type="button" class="upload-remove-button" @click="removeFile(file.key)">
                            {{ texts.removeFile || 'Remove' }}
                        </button>
                    </li>
                </ul>
            </div>

            <!-- Question -->
            <div class="form-group">
                <label for="question" class="required">{{ texts.question }}</label>
                <textarea id="question" v-model="formData.question" rows="5" :class="{ 'is-invalid': errors.question }"
                    @blur="validateField('question')"></textarea>
                <span v-if="errors.question" class="error-message">{{ errors.question }}</span>
            </div>

            <!-- Error banner -->
            <div v-if="submitError" class="error-banner" role="alert" v-html="props.errorMessage">
            </div>

            <!-- Submit -->
            <div class="form-actions">
                <button type="submit" class="btn btn-primary" :disabled="submitting">
                    <span v-if="submitting">...</span>
                    <span v-else>{{ texts.submit }}</span>
                </button>
            </div>
        </form>
    </div>
</template>

<script setup lang="ts">
import { reactive, ref, computed, onMounted } from 'vue';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import svLocale from 'i18n-iso-countries/langs/sv.json';
import type { ArtviseCaseTexts, ArtviseCaseFormData, ArtviseAttachment, CaseType } from '../../types/ArtviseCaseTypes';
import { submitCase } from './artviseCaseService';

countries.registerLocale(enLocale);
countries.registerLocale(svLocale);

interface Props {
    confirmationMessage?: string;
    errorMessage?: string;
    categoryId?: string;
    caseTypes?: string;
    inboxGuid?: string;
    showPersonnummer?: boolean;
    showCountries?: boolean;
    pnrCustomPropertyGuid?: string;
    strictPnrValidation?: boolean;
    countryCustomPropertyGuid?: string;
    showExternalCaseNumber?: boolean;
    externalCaseNumberCustomPropertyGuid?: string;
    showFileUpload?: boolean;
    maxUploadFileSizeMb?: number;
    acceptedFileTypes?: string;
    fileUploadInstructions?: string;
    language?: string;
}

const props = defineProps<Props>();

const texts: ArtviseCaseTexts = ArtviseCaseTexts;
const iconUrl = '/assets/svg/uhriconsprite.svg#icon-triangel-pil-ned';
const uploadFiles = ref<File[]>([]);
const attachmentsInput = ref<HTMLInputElement | null>(null);

const hasFileUploadInstructions = computed(() => {
    const instructions = props.fileUploadInstructions ?? '';
    return instructions
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim().length > 0;
});

const selectedFiles = computed(() =>
    uploadFiles.value.map((file) => ({
        key: `${file.name}-${file.size}-${file.lastModified}`,
        name: file.name,
    })));

const countryLanguage = computed(() => (props.language === 'se' ? 'sv' : 'en'));

// When strict validation is on, only the full personnummer format is accepted; otherwise a bare
// birth date is also fine, so the placeholder reflects both accepted formats.
const pnrPlaceholder = computed(() => (props.strictPnrValidation ? texts.pnrHelp : texts.pnrHelpBirthDate));

const countryOptions = computed(() => {
    const countryNames = countries.getNames(countryLanguage.value, { select: 'official' });
    return Object.entries(countryNames)
        .map(([code, name]) => ({ code, name }))
        .sort((a, b) => a.name.localeCompare(b.name));
});

const parsedCaseTypes = computed<CaseType[]>(() => {
    if (!props.caseTypes) return [];
    try {
        return JSON.parse(props.caseTypes);
    } catch {
        return [];
    }
});

const formData = reactive<ArtviseCaseFormData>({
    caseTypeId: '',
    categoryId: props.categoryId ?? '',
    inboxGuid: props.inboxGuid ?? '',
    pnrCustomPropertyGuid: props.pnrCustomPropertyGuid ?? '',
    strictPnrValidation: props.strictPnrValidation ?? false,
    countryCustomPropertyGuid: props.countryCustomPropertyGuid ?? '',
    externalCaseNumberCustomPropertyGuid: props.externalCaseNumberCustomPropertyGuid ?? '',
    email: '',
    surname: '',
    lastname: '',
    pnr: '',
    country: '',
    externalCaseNumber: '',
    question: '',
    maxUploadFileSizeMb: props.maxUploadFileSizeMb ?? 10,
    acceptedFileTypes: props.acceptedFileTypes ?? '',
    attachments: [],
});

onMounted(() => {
    if (parsedCaseTypes.value.length === 1) {
        formData.caseTypeId = parsedCaseTypes.value[0].id;
    }
});

const errors = reactive<Record<string, string>>({});
const submitting = ref(false);
const submitError = ref(false);
const showConfirmation = ref(false);

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Ported from uhr.core.Helpers.Forms.FormValidationHelper.IsValidSocialSecurityNumberOrBirthDate:
// accepts a full personnummer with a valid date and correct control digit (10/12 digits),
// or just a valid birthdate without control digit (6 or 8 digits).
const toDigitsOnly = (value: string): string => value.replace(/\s/g, '').replace(/-/g, '').replace(/\+/g, '');

const stripSocialSecurityOrBirthDate = (value: string): string => {
    if (!value) return '';
    const digits = toDigitsOnly(value);
    if (!/(\d{6})|(\d{10})|(\d{12})/.test(digits)) return '';
    return digits.length === 12 ? digits.substring(2) : digits;
};

const daysInMonth = (month: number, year: number): number => {
    switch (month) {
        case 2:
            return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 29 : 28;
        case 4:
        case 6:
        case 9:
        case 11:
            return 30;
        default:
            return 31;
    }
};

const isValidDateParts = (year: number, month: number, day: number): boolean =>
    month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth(month, year);

const isValidDateFromStripped = (stripped: string): boolean => {
    let date = stripped;
    if (date.length === 10) {
        date = date.substring(0, 6);
    } else if (date.length === 8) {
        date = date.substring(2);
    }
    if (date.length !== 6) return false;

    const year = parseInt(date.substring(0, 2), 10);
    const month = parseInt(date.substring(2, 4), 10);
    const day = parseInt(date.substring(4, 6), 10);
    return isValidDateParts(year, month, day);
};

const isCorrectControlNumber = (number: string): boolean => {
    let sum = 0;
    const digitsExceptLast = number.substring(0, number.length - 1);
    for (let index = 0; index < digitsExceptLast.length; index++) {
        const multiplier = index % 2 === 0 ? 2 : 1;
        let intValue = parseInt(digitsExceptLast[index], 10) * multiplier;
        if (intValue > 9) {
            const stringValue = intValue.toString();
            intValue = parseInt(stringValue[0], 10) + parseInt(stringValue[1], 10);
        }
        sum += intValue;
    }
    let controlNumber = 10 - (sum % 10);
    if (controlNumber === 10) controlNumber = 0;
    return number[number.length - 1] === controlNumber.toString();
};

const isValidSocialSecurityNumberOrBirthDate = (value: string): boolean => {
    const stripped = stripSocialSecurityOrBirthDate(value);
    if (!stripped) return false;

    const isNumber = /^[0-9]+$/.test(stripped);
    const isValidDate = isValidDateFromStripped(stripped);

    return isNumber && isValidDate && (isCorrectControlNumber(stripped) || stripped.length === 6 || stripped.length === 8);
};

const validateField = (field: string): boolean => {
    errors[field] = '';

    switch (field) {
        case 'caseTypeId':
            if (parsedCaseTypes.value.length > 1 && !formData.caseTypeId) {
                errors[field] = texts.validationRequired;
            }
            break;
        case 'email':
            if (!formData.email) {
                errors[field] = texts.validationRequired;
            } else if (!emailRegex.test(formData.email)) {
                errors[field] = texts.validationEmail;
            }
            break;
        case 'surname':
            if (!formData.surname.trim()) {
                errors[field] = texts.validationRequired;
            }
            break;
        case 'lastname':
            if (!formData.lastname.trim()) {
                errors[field] = texts.validationRequired;
            }
            break;
        case 'pnr':
            if (props.showPersonnummer && props.strictPnrValidation && formData.pnr) {
                if (!isValidSocialSecurityNumberOrBirthDate(formData.pnr)) {
                    errors[field] = texts.validationPnr;
                }
            }
            break;
        case 'country':
            if (props.showCountries && !formData.country) {
                errors[field] = texts.validationRequired;
            }
            break;
        case 'externalCaseNumber':
            break;
        case 'question':
            if (!formData.question.trim()) {
                errors[field] = texts.validationRequired;
            }
            break;
    }

    return !errors[field];
};

const validateForm = (): boolean => {
    const fields = ['caseTypeId', 'email', 'surname', 'lastname', 'question'];
    if (props.showPersonnummer) fields.push('pnr');
    if (props.showCountries) fields.push('country');
    let isValid = true;

    for (const field of fields) {
        if (!validateField(field)) {
            isValid = false;
        }
    }

    return isValid;
};

const parseAcceptedFileTypes = (): string[] =>
    (props.acceptedFileTypes ?? '')
        .split(',')
        .map((value) => value.trim().replace(/^\./, '').toLowerCase())
        .filter((value) => value.length > 0);

const acceptedFileTypes = computed(() => parseAcceptedFileTypes());
const acceptedFileTypesDisplay = computed(() => acceptedFileTypes.value.map((value) => `.${value}`).join(', '));
const acceptAttribute = computed(() => acceptedFileTypes.value.map((value) => `.${value}`).join(','));

const clearAttachmentError = () => {
    errors.attachments = '';
};

const removeFile = (key: string) => {
    uploadFiles.value = uploadFiles.value.filter((file) => `${file.name}-${file.size}-${file.lastModified}` !== key);
    clearAttachmentError();
    if (attachmentsInput.value) {
        attachmentsInput.value.value = '';
    }
};

const onFileChange = (event: Event) => {
    clearAttachmentError();
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    const maxBytes = (props.maxUploadFileSizeMb ?? 0) * 1024 * 1024;
    const existingKeys = new Set(uploadFiles.value.map((file) => `${file.name}-${file.size}-${file.lastModified}`));
    const additions: File[] = [];

    for (const file of files) {
        if (maxBytes > 0 && file.size > maxBytes) {
            errors.attachments = `${texts.validationFileSize} (${props.maxUploadFileSizeMb} MB).`;
            input.value = '';
            return;
        }

        if (acceptedFileTypes.value.length > 0) {
            const extension = file.name.includes('.') ? file.name.split('.').pop()?.toLowerCase() ?? '' : '';
            if (!acceptedFileTypes.value.includes(extension)) {
                errors.attachments = `${texts.validationFileType}: ${acceptedFileTypesDisplay.value}`;
                input.value = '';
                return;
            }
        }

        const key = `${file.name}-${file.size}-${file.lastModified}`;
        if (!existingKeys.has(key)) {
            additions.push(file);
            existingKeys.add(key);
        }
    }

    uploadFiles.value = [...uploadFiles.value, ...additions];
    input.value = '';
};

const toBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.includes(',') ? result.split(',')[1] : result;
        resolve(base64);
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
});

const handleSubmit = async () => {
    if (!validateForm()) {
        return;
    }

    submitting.value = true;
    submitError.value = false;

    try {
        const attachments: ArtviseAttachment[] = [];
        for (const file of uploadFiles.value) {
            attachments.push({
                fileName: file.name,
                contentType: file.type || 'application/octet-stream',
                dataBase64: await toBase64(file),
            });
        }

        const result = await submitCase({
            caseTypeId: formData.caseTypeId,
            categoryId: formData.categoryId,
            inboxGuid: formData.inboxGuid,
            pnrCustomPropertyGuid: formData.pnrCustomPropertyGuid,
            countryCustomPropertyGuid: formData.countryCustomPropertyGuid,
            externalCaseNumberCustomPropertyGuid: formData.externalCaseNumberCustomPropertyGuid,
            email: formData.email,
            surname: formData.surname.trim(),
            lastname: formData.lastname.trim(),
            pnr: props.showPersonnummer ? (formData.pnr || '') : '',
            strictPnrValidation: props.showPersonnummer ? !!props.strictPnrValidation : false,
            country: props.showCountries ? formData.country : '',
            externalCaseNumber: props.showExternalCaseNumber ? formData.externalCaseNumber.trim() : '',
            question: formData.question.trim(),
            maxUploadFileSizeMb: formData.maxUploadFileSizeMb,
            acceptedFileTypes: formData.acceptedFileTypes,
            attachments,
        });

        if (result.success) {
            showConfirmation.value = true;
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            submitError.value = true;
        }
    } catch {
        submitError.value = true;
    } finally {
        submitting.value = false;
    }
};
</script>
